import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, R, STAGE_CHIP, font } from '../theme/tokens';
import { discoverActivities, roleOf } from '../state/selectors';
import { dayLabel, ymdIn } from '../lib/dates';
import type { DiscoverTab } from '../state/reducer';
import { orderedSeries, seriesLabel } from '../domain/series';
import { Avatar, Btn, Card, Chip, ChipRow, EmptyState, Mono, Photo, Segmented, Tag, safeTop, ScreenTitle } from '../components/ui';

const TABS: readonly DiscoverTab[] = ['Activities', 'Help', 'People', 'UNITS'];
const STATUS = ['Any status', 'Idea', 'Planning', 'Scheduled', 'Live', 'Completed'] as const;

export function Discover() {
  const { state: { data, ui }, act } = useApp();
  const { tab, series, q, status } = ui.discover;
  const query = q.trim().toLowerCase();
  const match = (...xs: string[]) => !query || xs.join(' ').toLowerCase().includes(query);
  const catalogue = orderedSeries(data.series);
  const seriesOk = (s: string) => series === 'All' || s === series;
  const acts = discoverActivities(data, q, series, status);
  const help = Object.values(data.activities)
    .filter((a) => roleOf(data, a.id) === null && a.needs.length && a.stage < 4 && seriesOk(a.series) && match(a.name, a.city, a.country, ...a.needs))
    .flatMap((a) => a.needs.map((n) => ({ a, n })));
  // Each member once (keyed by id), under every affiliation the server sent for this viewer.
  const people = Object.values(data.ambassadors).filter((p) => p.id !== data.me && (series === 'All' || p.affiliations.includes(series)) && match(p.name, p.city, p.title, ...p.interests, ...p.skills));

  return (
    <div className="fade">
      <div style={{ padding: `${safeTop(20)} 20px 0`, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ScreenTitle right="WORLDWIDE">Discover</ScreenTitle>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, height: 50, borderRadius: R.pill, background: C.white, border: `1px solid ${C.border}`, padding: '0 18px' }}>
          <span aria-hidden="true" style={{ font: font(600, 16), color: C.grey }}>⌕</span>
          <input type="search" value={q} onChange={(e) => act.setDiscover({ q: e.target.value })} placeholder="Search people, cities, interests, activities" aria-label="Search"
            style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', font: font(400, 16), color: C.ink, minWidth: 0 }} />
        </label>
        <Segmented label="Discover sections" options={TABS} value={tab} onChange={(t) => act.setDiscover({ tab: t })} />
      </div>
      <ChipRow>{['All', ...catalogue.map((c) => c.id)].map((s) => <Chip key={s} label={s} selected={series === s} onClick={() => act.setDiscover({ series: s })} />)}</ChipRow>

      {tab === 'Activities' && (
        <>
          <ChipRow padding="8px 20px 0">{STATUS.map((s) => <Chip key={s} label={s} height={32} selected={status === s} onClick={() => act.setDiscover({ status: s })} />)}</ChipRow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 12px 0' }}>
            {acts.map((a) => {
              const role = roleOf(data, a.id);
              const [l, bg, fg] = STAGE_CHIP[a.stage];
              const fol = Boolean(data.followedActivities[a.id]);
              const org = data.ambassadors[a.organiserId];
              return (
                <article key={a.id} className="fade" style={{ background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.card, overflow: 'hidden' }}>
                  <button type="button" onClick={() => navigate({ name: 'activity', id: a.id })} aria-label={`Open ${a.name}`} style={{ display: 'block', width: '100%', position: 'relative', height: 170, border: 0, padding: 0, background: C.night }}>
                    <Photo id={a.photo} pad={20} fallback={a.series} sizes="(max-width: 480px) 94vw, 456px" />
                    <span style={{ position: 'absolute', top: 12, left: 12 }}><Tag label={l} bg={bg} fg={fg} radius={R.pill} /></span>
                    {role && <span style={{ position: 'absolute', top: 12, right: 12 }}><Tag label={`YOU: ${role.toUpperCase()}`} bg={C.mark} fg={C.ink} radius={R.pill} /></span>}
                  </button>
                  <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Mono size={10} color={C.violet}>{`${a.type} · ${seriesLabel(data.series, a.series)}`.toUpperCase()}</Mono>
                    <button type="button" onClick={() => navigate({ name: 'activity', id: a.id })} style={{ border: 0, background: 'transparent', padding: 0, textAlign: 'left', font: font(700, 21, 1.1), letterSpacing: '-.025em', color: C.ink }}>{a.name}</button>
                    <span style={{ font: font(400, 13, 1.4), color: C.grey }}>{`${dayLabel(ymdIn(a.startsAt, a.timeZone))} · ${a.city} · Organiser: ${a.organiserId === data.me ? 'you' : org?.name}`}</span>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {role ? (
                        <Btn variant="primary" onClick={() => act.openSheet({ kind: 'update', activityId: a.id })}>Update progress</Btn>
                      ) : (
                        <Btn variant={fol ? 'ink' : 'outline'} aria-pressed={fol} onClick={() => act.toggleFollowActivity(a.id)}>{fol ? 'Following ✓' : 'Follow'}</Btn>
                      )}
                      <Btn onClick={() => navigate({ name: 'activity', id: a.id })}>View activity</Btn>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          {!acts.length && <EmptyState title="No activities here yet." body="Be the first to organise one for this series." action={<Btn variant="primary" onClick={() => navigate({ name: 'create', sub: 'propose' })}>Propose an activity</Btn>} />}
        </>
      )}

      {tab === 'Help' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 12px 0' }}>
          <p style={{ margin: '0 8px 4px', font: font(400, 13, 1.45), color: C.grey }}>Teams asking for a hand. Offering help notifies the organiser; they confirm.</p>
          {help.map(({ a, n }) => {
            const on = Boolean(data.offeredHelp[a.id]);
            const org = data.ambassadors[a.organiserId];
            return (
              <div key={`${a.id}|${n}`} className="fade" style={{ background: C.ink, color: C.white, borderRadius: R.card, padding: 14, display: 'grid', gridTemplateColumns: '72px 1fr', gap: 14 }}>
                <button type="button" onClick={() => navigate({ name: 'activity', id: a.id })} aria-label={`Open ${a.name}`} style={{ width: 72, height: 92, border: 0, padding: 0, borderRadius: R.thumb, overflow: 'hidden', background: C.dark1 }}>
                  <Photo id={a.photo} pad={8} fallback={a.series.slice(0, 1)} sizes="72px" style={{ padding: a.photo ? undefined : 8 }} />
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                  <span style={{ font: font(700, 9), letterSpacing: '.14em', color: C.amberDark }}>{`HELP WANTED · ${a.series.toUpperCase()}`}</span>
                  <span style={{ font: font(700, 17, 1.2), letterSpacing: '-.015em' }}>{`${a.city} needs ${n.toLowerCase()}`}</span>
                  <span style={{ font: font(400, 12, 1.4), color: C.mutedDark }}>{`${a.name} · ${dayLabel(ymdIn(a.startsAt, a.timeZone))} · ${org.name}`}</span>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    <Btn size="sm" variant={on ? 'mark' : 'white'} aria-pressed={on} onClick={() => act.toggleOffer(a.id)}>{on ? 'Offered ✓' : 'Offer help'}</Btn>
                  </div>
                </div>
              </div>
            );
          })}
          {!help.length && <EmptyState title="No open requests match." body="Try another series or clear the search." />}
        </div>
      )}

      {tab === 'People' && (
        <Card style={{ margin: '16px 12px 0', padding: '6px 16px' }}>
          {people.map((p) => {
            const on = Boolean(data.followedPeople[p.id]);
            return (
              <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.divider}` }}>
                <button type="button" onClick={() => navigate({ name: 'profile', id: p.id })} aria-label={`Open ${p.name}`} style={{ border: 0, padding: 0, background: 'transparent' }}><Avatar person={p} size={48} /></button>
                <button type="button" onClick={() => navigate({ name: 'profile', id: p.id })} style={{ display: 'flex', flexDirection: 'column', gap: 2, border: 0, background: 'transparent', padding: 0, textAlign: 'left', minWidth: 0 }}>
                  <span style={{ font: font(600, 15), color: C.ink }}>{p.name}</span>
                  <span style={{ font: font(500, 11, 1.3), color: C.violet }}>{p.title}</span>
                  <span style={{ font: font(400, 11, 1.3), color: C.grey }}>{p.interests.slice(0, 3).join(' · ')}</span>
                </button>
                <Btn size="xs" variant={on ? 'ink' : 'outline'} aria-pressed={on} onClick={() => act.toggleFollowPerson(p.id)}>{on ? 'Following' : 'Follow'}</Btn>
              </div>
            );
          })}
          {!people.length && <p style={{ font: font(400, 13), color: C.grey }}>No ambassadors match.</p>}
        </Card>
      )}

      {tab === 'UNITS' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, padding: '16px 12px 0' }}>
          {catalogue.filter((u) => seriesOk(u.id)).map((u) => {
            const na = Object.values(data.activities).filter((a) => a.series === u.id).length;
            const np = Object.values(data.ambassadors).filter((p) => p.affiliations.includes(u.id)).length;
            const inv = u.status === 'launch';
            const coin = u.art?.startsWith('coin');
            return (
              <button key={u.id} type="button" onClick={() => act.setDiscover({ tab: 'Activities', series: u.id })} className="press"
                style={{ background: inv ? C.ink : C.white, color: inv ? C.white : C.ink, border: `1px solid ${C.subtle}`, borderRadius: R.pulse, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                <span style={{ display: 'block', height: 110, borderRadius: R.thumb, overflow: 'hidden', background: coin ? C.paper : C.night }}><Photo id={u.art} pad={8} fallback={u.id} sizes="200px" /></span>
                <span style={{ font: font(700, 16), letterSpacing: '-.02em' }}>{u.label}</span>
                <Mono size={10} color={inv ? C.rim : C.grey}>{u.status === 'launch' ? 'LAUNCH SERIES' : 'UPCOMING'}</Mono>
                <Mono size={10} color={inv ? C.mutedDark : C.grey}>{`${np} AMBASSADORS · ${na} ACTIVITIES`}</Mono>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
